import React from 'react';
import { motion } from 'framer-motion';
import { StarIcon } from '@heroicons/react/24/solid';

const testimonials = [
  {
    content: "Autiin's Channel Manager has revolutionized our booking process. We've increased our OTA bookings by 45% while reducing manual work by 60%. The platform is intuitive and the support team is exceptional.",
    author: "Sarah Johnson",
    role: "Operations Director",
    company: "Luxury Boutique Hotels",
    rating: 5,
    product: "Channel Manager"
  },
  {
    content: "The Hotel Management Software from Autiin has streamlined our entire operation. From reservations to billing, everything is now centralized and automated. Our staff productivity has increased dramatically.",
    author: "Michael Chen",
    role: "General Manager",
    company: "Grand Plaza Hotels",
    rating: 5,
    product: "Hotel Management"
  },
  {
    content: "As a growing hospitality business, we needed a solution that could scale with us. Autiin's platform has been perfect - it's reliable, secure, and the cross-platform access is a game-changer for our mobile workforce.",
    author: "Emma Rodriguez",
    role: "CEO",
    company: "Seaside Resorts",
    rating: 5,
    product: "Complete Suite"
  },
  {
    content: "The integration capabilities are outstanding. We've connected Autiin with our existing systems seamlessly. The real-time synchronization across all platforms has eliminated data inconsistencies.",
    author: "David Thompson",
    role: "IT Director",
    company: "Metropolitan Hotels",
    rating: 5,
    product: "Integration Services"
  },
  {
    content: "Customer support is top-notch. The team at Autiin truly understands the hospitality industry and has been instrumental in our digital transformation journey.",
    author: "Lisa Wang",
    role: "Revenue Manager",
    company: "Urban Boutique Collection",
    rating: 5,
    product: "Support Services"
  },
  {
    content: "We've seen a 30% increase in operational efficiency since implementing Autiin's solutions. The analytics and reporting features provide insights we never had before.",
    author: "Robert Martinez",
    role: "Operations Manager",
    company: "Coastal Resorts",
    rating: 5,
    product: "Analytics Platform"
  }
];

export default function Testimonials() {
  return (
    <section className="py-24 sm:py-32 bg-gradient-to-b from-gray-50 to-white">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div className="mx-auto max-w-2xl text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-base font-semibold leading-7 text-primary-600">Testimonials</h2>
            <p className="mt-2 text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
              Trusted by Leading Businesses
            </p>
            <p className="mt-6 text-lg leading-8 text-gray-600">
              See what our clients say about how Autiin solutions have transformed their operations 
              and driven measurable results.
            </p>
          </motion.div>
        </div>

        <div className="mx-auto mt-16 max-w-2xl sm:mt-20 lg:mt-24 lg:max-w-none">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-2 xl:grid-cols-3">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="group relative bg-white rounded-2xl shadow-lg border border-gray-100 p-8 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
              >
                {/* Rating */}
                <div className="flex items-center mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <StarIcon key={i} className="h-5 w-5 text-accent-400" />
                  ))}
                </div>

                {/* Product Badge */}
                <div className="inline-flex items-center rounded-full bg-primary-50 px-3 py-1 text-xs font-medium text-primary-700 mb-4">
                  {testimonial.product}
                </div>

                {/* Quote */}
                <blockquote className="text-gray-900 mb-6">
                  <p className="text-base leading-relaxed">"{testimonial.content}"</p>
                </blockquote>

                {/* Author */}
                <div className="flex items-center">
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-r from-primary-600 to-secondary-600 text-white font-semibold text-lg">
                    {testimonial.author.split(' ').map(n => n[0]).join('')}
                  </div>
                  <div className="ml-4">
                    <div className="font-semibold text-gray-900">{testimonial.author}</div>
                    <div className="text-sm text-gray-600">{testimonial.role}</div>
                    <div className="text-sm text-primary-600 font-medium">{testimonial.company}</div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Stats Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          viewport={{ once: true }}
          className="mt-24 sm:mt-32"
        >
          <div className="bg-gradient-to-r from-primary-600 to-secondary-600 rounded-3xl p-8 sm:p-12">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 text-center">
              {[
                { number: '50+', label: 'Happy Clients' },
                { number: '98%', label: 'Satisfaction Rate' },
                { number: '24/7', label: 'Support Available' },
                { number: '99.9%', label: 'Uptime Guarantee' }
              ].map((stat, index) => (
                <motion.div
                  key={stat.label}
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <div className="text-3xl font-bold text-white mb-2">{stat.number}</div>
                  <div className="text-primary-100 text-sm font-medium">{stat.label}</div>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
} 